import { useMemo } from "react";
import PortalLayout from "@/components/PortalLayout";
import { resolveClientKey, getClientVessels, MOCK_VESSELS, MOCK_EMISSIONS } from "@/data/portalMockData";
import { Leaf, TrendingDown, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { usePortalCompany } from "@/hooks/usePortalCompany";

const ESGEmissions = () => {
  const { companyName } = usePortalCompany();
  const clientKey = resolveClientKey(companyName);
  const clientVessels = useMemo(() => clientKey ? getClientVessels(clientKey) : MOCK_VESSELS, [clientKey]);

  const vesselKeys = useMemo(() =>
    clientVessels.map(v => v.name.replace("Velocity ", "").toLowerCase()),
    [clientVessels]
  );

  const fuelEfficiency = useMemo(() =>
    clientVessels.slice(0, 5).map((v, i) => ({
      vessel: v.name,
      lPerNm: Math.round((12.4 + i * 0.3) * 100) / 100,
      delta: Math.round((-1.2 + i * 0.5) * 100) / 100,
    })),
    [clientVessels]
  );
  const fleetAvg = useMemo(() => {
    if (!fuelEfficiency.length) return 0;
    return Math.round((fuelEfficiency.reduce((s, v) => s + v.lPerNm, 0) / fuelEfficiency.length) * 100) / 100;
  }, [fuelEfficiency]);

  return (
    <PortalLayout>
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[3px] uppercase text-[hsl(211,100%,40%)]/60 mb-1">Sustainability</p>
        <h1 className="text-3xl font-bold text-[hsl(215,25%,17%)]">ESG & Emissions</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="portal-card p-5 text-center">
          <Leaf className="w-6 h-6 text-[#28A745] mx-auto mb-2" />
          <p className="text-3xl font-bold text-[hsl(215,25%,17%)]">{MOCK_EMISSIONS.totalThisMonth} MT</p>
          <p className="text-[10px] text-[hsl(215,12%,50%)]">Total CO₂ This Month</p>
        </div>
        <div className="portal-card p-5 text-center">
          <p className="text-3xl font-bold text-[hsl(215,25%,17%)]">{MOCK_EMISSIONS.efficiency}</p>
          <p className="text-[10px] text-[hsl(215,12%,50%)]">gCO₂/pax-nm Efficiency</p>
        </div>
        <div className="portal-card p-5 text-center">
          <p className={`text-3xl font-bold ${MOCK_EMISSIONS.momChange < 0 ? "text-[#28A745]" : "text-[#DC3545]"}`}>{MOCK_EMISSIONS.momChange}%</p>
          <p className="text-[10px] text-[hsl(215,12%,50%)]">Month-on-Month Change</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="portal-card p-4">
          <h3 className="font-bold text-sm mb-3">Monthly Emissions by Vessel (MT CO₂)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={MOCK_EMISSIONS.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              {vesselKeys.slice(0, 4).map((key, i) => {
                const colors = ["#0066CC", "#28A745", "#FFC107", "#17A2B8", "#DC3545", "#6F42C1", "#FD7E14", "#E83E8C"];
                return <Bar key={key} dataKey={key} name={key.charAt(0).toUpperCase() + key.slice(1)} fill={colors[i % colors.length]} radius={[3, 3, 0, 0]} />;
              })}
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="portal-card p-4">
          <h3 className="font-bold text-sm mb-3">Fuel Efficiency Leaderboard (L/nm)</h3>
          <div className="space-y-3">
            {fuelEfficiency.map((v, i) => (
              <div key={v.vessel} className="flex items-center gap-3">
                <span className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 ? "bg-[#28A745] text-white" : "bg-[hsl(220,20%,97%)] text-[hsl(215,25%,17%)]"}`}>{i + 1}</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{v.vessel}</span>
                    <span className="text-sm font-bold">{v.lPerNm}</span>
                  </div>
                  <div className="w-full bg-[hsl(220,15%,90%)] rounded-full h-1.5 mt-1">
                    <div className="h-1.5 rounded-full bg-[hsl(211,100%,40%)]" style={{ width: `${(1 - (v.lPerNm - 12) / 4) * 100}%` }} />
                  </div>
                </div>
                <span className={`text-[10px] font-bold ${v.delta < 0 ? "text-[#28A745]" : "text-[#DC3545]"}`}>
                  {v.delta > 0 ? "+" : ""}{v.delta} vs avg
                </span>
              </div>
            ))}
            <p className="text-[10px] text-[hsl(215,12%,50%)] text-center">Fleet Average: {fleetAvg} L/nm</p>
          </div>
        </div>
      </div>

      <div className="portal-card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-sm">ESG Report</h3>
          <Button variant="outline" size="sm" onClick={() => toast({ title: "Report Downloaded", description: "Monthly ESG report PDF ready." })}>
            <Download className="w-3.5 h-3.5 mr-1" /> Download Monthly ESG Report (PDF)
          </Button>
        </div>
        <p className="text-xs text-[hsl(215,12%,50%)]">Formatted summary for your sustainability reporting. Includes CO₂ emissions, fuel efficiency metrics, and voyage-level data.</p>
      </div>
    </PortalLayout>
  );
};

export default ESGEmissions;
